import DOMPurify from 'dompurify';

/**
 * Sanitizes an HTML string before it is injected into the DOM.
 *
 * Use this for every value passed to `dangerouslySetInnerHTML` so that any
 * untrusted markup (e.g. <script> tags, inline event handlers) is stripped
 * out and cannot lead to XSS.
 *
 * @param {string} html - The raw HTML string to sanitize
 * @returns {string} The sanitized HTML string (empty string for non-string input)
 */
export const sanitizeHtml = (html) => {
  if (typeof html !== 'string') {
    return '';
  }

  return DOMPurify.sanitize(html);
};

/**
 * Sanitizes a region code to prevent path traversal attacks
 * @param {string} region - The region code to sanitize
 * @returns {string} The sanitized region code
 * @throws {Error} If the region code contains invalid characters
 */
export const sanitizeRegionCode = (region) => {
  if (!region || typeof region !== 'string') {
    throw new Error('Invalid region code: must be a non-empty string');
  }

  // Only allow alphanumeric characters, underscores, hyphens, and commas
  // This prevents path traversal attacks using ../ or similar sequences
  const safeRegionPattern = /^[a-zA-Z0-9_,-]+$/;

  if (!safeRegionPattern.test(region)) {
    throw new Error(`Invalid region code: "${region}" contains invalid characters. Only alphanumeric, underscore, hyphen, and comma are allowed.`);
  }

  return region;
};
